import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Box, Typography, Paper, Grid, Button, IconButton, Chip,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, FormControl, InputLabel,
  ListItemText, ListItemIcon, ListItemButton,
  Collapse, Divider, Tooltip, Badge, Card, CardContent, CardActions,
  Breadcrumbs, Link, Stepper, Step, StepLabel, StepContent,
  Stack, Alert, Snackbar, CircularProgress, Skeleton,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";

import SearchIcon          from "@mui/icons-material/Search";
import AddIcon             from "@mui/icons-material/Add";
import EditIcon            from "@mui/icons-material/Edit";
import DeleteIcon          from "@mui/icons-material/Delete";
import PrintIcon           from "@mui/icons-material/Print";
import ContentCopyIcon     from "@mui/icons-material/ContentCopy";
import VisibilityIcon      from "@mui/icons-material/Visibility";
import ExpandLessIcon      from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon      from "@mui/icons-material/ExpandMore";
import DescriptionIcon     from "@mui/icons-material/Description";
import PolicyIcon          from "@mui/icons-material/Policy";
import AssignmentIcon      from "@mui/icons-material/Assignment";
import LocalHospitalIcon   from "@mui/icons-material/LocalHospital";
import ScienceIcon         from "@mui/icons-material/Science";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SecurityIcon        from "@mui/icons-material/Security";
import CloseIcon           from "@mui/icons-material/Close";
import CheckCircleIcon     from "@mui/icons-material/CheckCircle";
import BookmarkIcon        from "@mui/icons-material/Bookmark";
import GroupIcon           from "@mui/icons-material/Group";
import CalendarTodayIcon   from "@mui/icons-material/CalendarToday";
import PersonIcon          from "@mui/icons-material/Person";
import ArrowBackIcon       from "@mui/icons-material/ArrowBack";
import DomainIcon          from "@mui/icons-material/Domain";
import RefreshIcon         from "@mui/icons-material/Refresh";

import { loadAllSOPDocumentsdb, loadSOPDocumentByIddb, createSOPDocumentdb, updateSOPDocumentdb, deleteSOPDocumentdb, uploadSopDocumentdb } from "../BaseURL";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

// ─── theme ────────────────────────────────────────────────────────────────────
const PRIMARY      = "#1A9B7F";
const PRIMARY_DARK = "#157763";
const theme = createTheme({
  palette: {
    primary:    { main: PRIMARY, dark: PRIMARY_DARK, contrastText: "#fff" },
    background: { default: "#F4F6F9", paper: "#ffffff" },
  },
  typography: { fontFamily: "'Segoe UI', 'Roboto', sans-serif" },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: "none", borderRadius: 8, fontWeight: 600 } } },
    MuiChip:   { styleOverrides: { root: { fontWeight: 600 } } },
  },
});

// ─── sidebar tree ─────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: "governance", label: "Hospital Governance", icon: <DomainIcon />, children: [
    { id: "gov-infection", label: "Infection Control"  },
    { id: "gov-safety",    label: "Patient Safety"     },
    { id: "gov-data",      label: "Data Protection"    },
    { id: "gov-staff",     label: "Staff Welfare"      },
  ]},
  { id: "clinical", label: "Clinical SOPs", icon: <LocalHospitalIcon />, children: [
    { id: "cli-admission",  label: "Admission & Discharge" },
    { id: "cli-medication", label: "Medication"            },
    { id: "cli-surgical",   label: "Surgical Safety"       },
    { id: "cli-lab",        label: "Laboratory"            },
    { id: "cli-emergency",  label: "Emergency"             },
  ]},
  { id: "pharmacy", label: "Pharmacy Procedures", icon: <MedicalServicesIcon />, children: [
    { id: "pha-dispensing", label: "Dispensing"   },
    { id: "pha-storage",    label: "Drug Storage" },
  ]},
  { id: "lab", label: "Laboratory Protocols", icon: <ScienceIcon />, children: [
    { id: "lab-blood",   label: "Blood Collection"      },
    { id: "lab-culture", label: "Culture & Sensitivity" },
  ]},
  { id: "security", label: "Security & Privacy", icon: <SecurityIcon />, children: [
    { id: "sec-data",   label: "Data Privacy"   },
    { id: "sec-access", label: "Access Control" },
  ]},
];

function flatCategories() {
  return CATEGORIES.flatMap(cat =>
    cat.children.map(child => ({ id: child.id, label: `${cat.label} › ${child.label}` }))
  );
}

const TYPE_COLORS   = { SOP: "#1A9B7F", Policy: "#2563eb" };
const STATUS_COLORS = { Active: "success", "Under Review": "warning", Archived: "default", Draft: "info" };

const EMPTY_FORM = {
  title: "", type: "SOP", category: "cli-medication", category_label: "",
  department: "All Wards", version: "v1.0", status: "Draft",
  review_date: "", author: "", approved_by: "", description: "",
  tags: "", steps: [{ label: "", content: "" }],
};

// ─── normalise DB row ─────────────────────────────────────────────────────────
function normaliseDoc(row) {
  return {
    ...row,
    id:            Number(row.id),
    tags:          row.tags ? row.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
    steps:         row.steps || [],
    review_date:   row.review_date ? row.review_date.split("T")[0] : "",
    created_at:    row.created_at  ? row.created_at.split("T")[0]  : "",
    categoryLabel: row.category_label || row.category,
  };
}

// ─── print ────────────────────────────────────────────────────────────────────
function handlePrint(doc) {
  const win = window.open("", "_blank");
  win.document.write(`<!DOCTYPE html><html><head>
    <title>${doc.title}</title>
    <style>
      body{font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:40px auto;color:#222}
      .header{border-bottom:3px solid #1A9B7F;padding-bottom:16px;margin-bottom:24px}
      h1{color:#1A9B7F;margin:0 0 8px}
      .meta{display:flex;gap:20px;font-size:13px;color:#555;flex-wrap:wrap}
      .badge{background:#1A9B7F;color:#fff;padding:2px 10px;border-radius:12px;font-size:12px}
      .badge-p{background:#2563eb}
      .step{margin:16px 0;padding:16px;border-left:4px solid #1A9B7F;background:#f4faf7;border-radius:4px}
      .sn{font-weight:700;color:#1A9B7F}
      .footer{margin-top:40px;font-size:11px;color:#888;border-top:1px solid #ddd;padding-top:12px}
    </style></head><body>
    <div class="header">
      <h1>${doc.title}</h1>
      <div class="meta">
        <span><b>Type:</b> <span class="badge ${doc.type === "Policy" ? "badge-p" : ""}">${doc.type}</span></span>
        <span><b>Version:</b> ${doc.version}</span>
        <span><b>Status:</b> ${doc.status}</span>
        <span><b>Department:</b> ${doc.department}</span>
        <span><b>Author:</b> ${doc.author}</span>
        <span><b>Approved by:</b> ${doc.approved_by}</span>
        <span><b>Review Date:</b> ${doc.review_date}</span>
      </div>
      <p style="margin:8px 0 0;font-style:italic;color:#444">${doc.description}</p>
    </div>
    ${(doc.steps || []).map((s, i) => `
      <div class="step">
        <div class="sn">Step ${i + 1}: ${s.label}</div>
        <p style="margin:6px 0 0">${s.content}</p>
      </div>`).join("")}
    <div class="footer">
      Printed from TotalCare Hospital Management System &bull;
      ${new Date().toLocaleDateString()} &bull; ${doc.categoryLabel}
    </div></body></html>`);
  win.document.close();
  win.print();
}

function buildCopyText(doc) {
  return [
    `TITLE: ${doc.title}`,
    `TYPE: ${doc.type}   VERSION: ${doc.version}   STATUS: ${doc.status}`,
    `DEPARTMENT: ${doc.department}`,
    `AUTHOR: ${doc.author}   APPROVED BY: ${doc.approved_by}`,
    `REVIEW DATE: ${doc.review_date}`,
    ``,
    `DESCRIPTION:\n${doc.description}`,
    ``,
    ...(doc.steps || []).map((s, i) => `STEP ${i + 1}: ${s.label}\n${s.content}`),
    ``,
    `Category: ${doc.categoryLabel}`,
    `Generated from TotalCare HIS — ${new Date().toLocaleDateString()}`,
  ].join("\n");
}

// ─── API calls ────────────────────────────────────────────────────────────────
const loadAllSOPDocuments = () =>
  axios.get(loadAllSOPDocumentsdb);

const loadSOPDocumentById = (id) =>
  axios.get(loadSOPDocumentByIddb, { params: { id } });

const createSOPDocument = (body) =>
  axios.post(createSOPDocumentdb, body);

const updateSOPDocument = (body) =>
  axios.post(updateSOPDocumentdb, body);

const deleteSOPDocument = (id) =>
  axios.post(deleteSOPDocumentdb, { id });

// ═════════════════════════════════════════════════════════════════════════════
//  COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function PolicySOPManagement() {
  const [docs,         setDocs]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [viewDoc,      setViewDoc]      = useState(null);
  const [viewLoad,     setViewLoad]     = useState(false);
  const [editId,       setEditId]       = useState(null);   // null | "new" | number
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [selectedCat,  setSelectedCat]  = useState(null);
  const [openCats,     setOpenCats]     = useState({ governance: true, clinical: true });
  const [search,       setSearch]       = useState("");
  const [filterType,   setFilterType]   = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

const [uploadState, setUploadState] = useState({
  loading:  false,
  file:     null,
  dragOver: false,
});

  const flatCats = flatCategories();

  // ── get current user from localStorage (your pattern) ─────────────────────
  const currentUser = JSON.parse(localStorage.getItem("userInfo") || "{}")?.username || "unknown";

  function toast(msg, sev = "success") { setSnack({ open: true, msg, sev }); }

  // ── load all ───────────────────────────────────────────────────────────────
  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await loadAllSOPDocuments();
      if (data.success) {
        setDocs(data.data.map(normaliseDoc));
      } else {
        toast(data.message || "Failed to load documents", "error");
      }
    } catch (error) {
      console.error("loadAllSOPDocuments error:", error);
      toast(error.response?.data?.message || error.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // ── open detail view ───────────────────────────────────────────────────────
  async function openView(id) {
    setViewLoad(true);
    try {
      const { data } = await loadSOPDocumentById(id);
      if (data.success) {
        setViewDoc(normaliseDoc(data.data));
      } else {
        toast(data.message || "Could not load document", "error");
      }
    } catch (error) {
      console.error("loadSOPDocumentById error:", error);
      toast(error.response?.data?.message || error.message, "error");
    } finally {
      setViewLoad(false);
    }
  }

  // ── open edit dialog ───────────────────────────────────────────────────────
  async function openEdit(docOrId) {
    let doc = typeof docOrId === "object" ? docOrId : null;

    // always fetch full doc (with steps) before editing
    try {
      const id = typeof docOrId === "object" ? docOrId.id : docOrId;
      const { data } = await loadSOPDocumentById(id);
      if (data.success) doc = normaliseDoc(data.data);
    } catch { /* fall through with partial */ }

    if (!doc) return;

    setForm({
      title:          doc.title          || "",
      type:           doc.type           || "SOP",
      category:       doc.category       || "cli-medication",
      category_label: doc.category_label || "",
      department:     doc.department     || "",
      version:        doc.version        || "v1.0",
      status:         doc.status         || "Draft",
      review_date:    doc.review_date    || "",
      author:         doc.author         || "",
      approved_by:    doc.approved_by    || "",
      description:    doc.description    || "",
      tags: Array.isArray(doc.tags) ? doc.tags.join(", ") : (doc.tags || ""),
      steps: doc.steps?.length
        ? doc.steps.map(s => ({ label: s.label, content: s.content }))
        : [{ label: "", content: "" }],
    });
    setEditId(doc.id);
  }

  function openCreate() {
  setForm({ ...EMPTY_FORM, category: selectedCat || "cli-medication" });
  setUploadState({ loading: false, file: null, dragOver: false });
  setEditId("new");
}

  // ── save ───────────────────────────────────────────────────────────────────
  async function saveDoc() {
    if (!form.title.trim()) { toast("Title is required", "error"); return; }

    const catEntry  = flatCats.find(c => c.id === form.category);
    const tagsArray = form.tags.split(",").map(t => t.trim()).filter(Boolean);

    const payload = {
      ...form,
      id:             editId === "new" ? undefined : editId,
      category_label: catEntry?.label?.split(" › ")[1] || form.category,
      tags:           tagsArray,
      steps:          form.steps.filter(s => s.label.trim()),
      created_by:     currentUser,
    };

    setSaving(true);
    try {
      const { data } = editId === "new"
        ? await createSOPDocument(payload)
        : await updateSOPDocument(payload);

      if (!data.success) { toast(data.message || "Save failed", "error"); return; }

      toast(editId === "new" ? "Document created successfully" : "Document updated successfully");
      setEditId(null);
      loadDocs();
      if (viewDoc && viewDoc.id === editId) setViewDoc(null);

    } catch (error) {
      console.error("saveDoc error:", error);
      toast(error.response?.data?.message || error.message, "error");
    } finally {
      setSaving(false);
    }
  }

  // ── delete ─────────────────────────────────────────────────────────────────
  async function confirmDelete(doc) {
    const result = await Swal.fire({
      title: "Delete Document?",
      html:  `<b>${doc.title}</b><br><small style="color:#666">This action cannot be undone.</small>`,
      icon:  "warning",
      showCancelButton:   true,
      confirmButtonColor: "#d33",
      cancelButtonColor:  "#aaa",
      confirmButtonText:  "Yes, delete it",
    });
    if (!result.isConfirmed) return;

    try {
      const { data } = await deleteSOPDocument(doc.id);
      if (!data.success) { toast(data.message || "Delete failed", "error"); return; }
      toast("Document deleted", "info");
      if (viewDoc?.id === doc.id) setViewDoc(null);
      loadDocs();
    } catch (error) {
      console.error("deleteSOPDocument error:", error);
      toast(error.response?.data?.message || error.message, "error");
    }
  }

  // ── step helpers ──────────────────────────────────────────────────────────
  function addStep()           { setForm(f => ({ ...f, steps: [...f.steps, { label: "", content: "" }] })); }
  function removeStep(i)       { setForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) })); }
  function updateStep(i, k, v) { setForm(f => { const s = [...f.steps]; s[i] = { ...s[i], [k]: v }; return { ...f, steps: s }; }); }



  // ── File upload → pre-fill form ────────────────────────────────────────────
async function handleFileUpload(file) {
  if (!file) return;
  setUploadState(s => ({ ...s, loading: true, file }));

  const formData = new FormData();
  formData.append('file', file);

  try {
    const { data } = await axios.post(uploadSopDocumentdb, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    if (!data.success) {
      toast(data.message || 'Could not parse file', 'error');
      return;
    }

    // Pre-fill form with extracted content
    setForm(f => ({
      ...f,
      title:       data.title       || f.title,
      description: data.description || f.description,
      tags:        data.tags        || f.tags,
      steps:       data.steps?.length ? data.steps : f.steps,
    }));

    toast(`✓ Extracted "${data.title}" from ${file.name}`, 'success');
  } catch (error) {
    toast(error.response?.data?.message || 'Upload failed', 'error');
  } finally {
    setUploadState(s => ({ ...s, loading: false }));
  }
}

  // ── filtered docs ──────────────────────────────────────────────────────────
  const filtered = docs.filter(d => {
    const s = search.toLowerCase();
    const matchSearch = !s ||
      d.title.toLowerCase().includes(s) ||
      (d.description || "").toLowerCase().includes(s) ||
      d.tags.some(t => t.toLowerCase().includes(s));
    const matchCat    = !selectedCat || d.category === selectedCat;
    const matchType   = filterType   === "All" || d.type   === filterType;
    const matchStatus = filterStatus === "All" || d.status === filterStatus;
    return matchSearch && matchCat && matchType && matchStatus;
  });

  const stats = {
    total:    docs.length,
    sops:     docs.filter(d => d.type   === "SOP").length,
    policies: docs.filter(d => d.type   === "Policy").length,
    review:   docs.filter(d => d.status === "Under Review").length,
  };

  // ══════════════════════════════════════════════════════════════════════════
  //  DETAIL VIEW
  // ══════════════════════════════════════════════════════════════════════════
  if (viewDoc) {
    const d = viewDoc;
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: { xs: 2, md: 3 } }}>

          {/* toolbar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <IconButton onClick={() => setViewDoc(null)}><ArrowBackIcon /></IconButton>
            <Breadcrumbs sx={{ flex: 1 }}>
              <Link color="primary" sx={{ cursor: "pointer", textDecoration: "none" }} onClick={() => setViewDoc(null)}>
                Policy &amp; SOP Library
              </Link>
              <Typography color="text.primary" noWrap sx={{ maxWidth: 320 }}>{d.title}</Typography>
            </Breadcrumbs>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Copy">
                <IconButton onClick={() => { navigator.clipboard?.writeText(buildCopyText(d)); toast("Copied to clipboard"); }}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton onClick={() => handlePrint(d)}><PrintIcon /></IconButton>
              </Tooltip>
              <Button startIcon={<EditIcon />} variant="outlined" size="small"
                onClick={() => { openEdit(d); setViewDoc(null); }}>Edit</Button>
              <Button startIcon={<DeleteIcon />} variant="outlined" color="error" size="small"
                onClick={() => confirmDelete(d)}>Delete</Button>
            </Stack>
          </Box>

          <Grid container spacing={3}>
            {/* header */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 3, borderLeft: `6px solid ${TYPE_COLORS[d.type]}` }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Chip label={d.type} size="small" sx={{ bgcolor: TYPE_COLORS[d.type], color: "#fff", fontWeight: 700 }} />
                      <Chip label={d.status} size="small" color={STATUS_COLORS[d.status]} variant="outlined" />
                      <Chip label={d.version} size="small" variant="outlined" />
                    </Stack>
                    <Typography variant="h5" fontWeight={700} gutterBottom>{d.title}</Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>{d.description}</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {d.tags.map(tag => (
                        <Chip key={tag} label={`#${tag}`} size="small"
                          sx={{ bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY_DARK }} />
                      ))}
                    </Stack>
                  </Box>
                  <Box sx={{ minWidth: 220 }}>
                    <Stack spacing={1}>
                      {[
                        [<PersonIcon sx={{ fontSize: 16 }} />,        "Author",      d.author],
                        [<CheckCircleIcon sx={{ fontSize: 16 }} />,   "Approved by", d.approved_by],
                        [<GroupIcon sx={{ fontSize: 16 }} />,         "Department",  d.department],
                        [<CalendarTodayIcon sx={{ fontSize: 16 }} />, "Review Date", d.review_date],
                        [<BookmarkIcon sx={{ fontSize: 16 }} />,      "Category",    d.categoryLabel],
                      ].map(([icon, label, val]) => (
                        <Stack key={label} direction="row" spacing={1} alignItems="center">
                          <Box color="text.secondary">{icon}</Box>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>{label}:</Typography>
                          <Typography variant="body2" fontWeight={600}>{val}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* steps */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: PRIMARY }}>
                  {d.type === "SOP" ? "Procedure Steps" : "Policy Sections"}
                </Typography>
                {d.steps.length === 0
                  ? <Typography color="text.secondary" fontStyle="italic">No steps added yet.</Typography>
                  : (
                    <Stepper orientation="vertical" nonLinear>
                      {d.steps.map((step, i) => (
                        <Step key={i} active>
                          <StepLabel StepIconProps={{ style: { color: PRIMARY } }}>
                            <Typography fontWeight={700}>{step.label}</Typography>
                          </StepLabel>
                          <StepContent>
                            <Paper sx={{ p: 2, bgcolor: alpha(PRIMARY, 0.04), borderLeft: `3px solid ${PRIMARY}`, borderRadius: 2, mt: 0.5 }}>
                              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>{step.content}</Typography>
                            </Paper>
                          </StepContent>
                        </Step>
                      ))}
                    </Stepper>
                  )}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Snackbar open={snack.open} autoHideDuration={3000}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </ThemeProvider>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  LIBRARY VIEW
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex", height: "100vh", overflow: "hidden", bgcolor: "background.default" }}>

        {/* ══════════ SIDEBAR ══════════ */}
        <Box sx={{
          width: 255, flexShrink: 0, bgcolor: "#fff",
          borderRight: "1px solid", borderColor: "divider",
          display: "flex", flexDirection: "column",
          height: "100vh", overflow: "hidden",
        }}>

          {/* ── logo header ── */}
          <Box sx={{ px: 2.5, py: 2, bgcolor: PRIMARY, flexShrink: 0 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha("#fff", 0.18), display: "flex", alignItems: "center", justifyContent: "center" }}>
                <PolicyIcon sx={{ color: "#fff", fontSize: 22 }} />
              </Box>
              <Box>
                <Typography fontSize={11} color={alpha("#fff", 0.75)} fontWeight={500} lineHeight={1.2}>TotalCare HIS</Typography>
                <Typography fontSize={13} color="#fff" fontWeight={700} lineHeight={1.3}>Policy &amp; SOP Library</Typography>
              </Box>
            </Stack>
          </Box>

          {/* ── stats bar ── */}
          <Box sx={{ px: 1.5, py: 1.5, bgcolor: alpha(PRIMARY, 0.06), borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}>
            <Grid container>
              {[["Total", stats.total, PRIMARY], ["SOPs", stats.sops, "#059669"], ["Policies", stats.policies, "#2563eb"], ["Review", stats.review, "#d97706"]].map(([l, v, c]) => (
                <Grid item xs={3} key={l}>
                  <Box sx={{ textAlign: "center", py: 0.5 }}>
                    {loading
                      ? <Skeleton variant="text" width={20} sx={{ mx: "auto" }} />
                      : <Typography fontWeight={800} fontSize={17} color={c} lineHeight={1.1}>{v}</Typography>}
                    <Typography fontSize={10} color="text.secondary" fontWeight={500}>{l}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* ── nav (scrollable) — All Docs + category tree in ONE container ── */}
          <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>

            {/* All Documents */}
            <ListItemButton
              selected={!selectedCat}
              onClick={() => setSelectedCat(null)}
              sx={{
                mx: 1, mb: 0.5, borderRadius: 2, minHeight: 40,
                "&.Mui-selected": { bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY },
                "&.Mui-selected:hover": { bgcolor: alpha(PRIMARY, 0.15) },
              }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <AssignmentIcon sx={{ fontSize: 18, color: !selectedCat ? PRIMARY : "text.secondary" }} />
              </ListItemIcon>
              <ListItemText
                primary="All Documents"
                primaryTypographyProps={{ fontSize: 13, fontWeight: !selectedCat ? 700 : 500 }}
              />
              <Chip
                label={docs.length}
                size="small"
                sx={{ height: 18, fontSize: 10, fontWeight: 700,
                  bgcolor: !selectedCat ? PRIMARY : alpha(PRIMARY, 0.1),
                  color: !selectedCat ? "#fff" : PRIMARY }}
              />
            </ListItemButton>

            <Divider sx={{ mx: 2, my: 1 }} />

            {/* Category groups */}
            {CATEGORIES.map((cat) => {
              const catCount = docs.filter(d => cat.children.some(c => c.id === d.category)).length;
              return (
                <Box key={cat.id}>
                  {/* group header */}
                  <ListItemButton
                    onClick={() => setOpenCats(p => ({ ...p, [cat.id]: !p[cat.id] }))}
                    sx={{ mx: 1, borderRadius: 2, minHeight: 36, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {React.cloneElement(cat.icon, { sx: { fontSize: 16, color: PRIMARY } })}
                    </ListItemIcon>
                    <ListItemText
                      primary={cat.label}
                      primaryTypographyProps={{ fontSize: 11.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}
                    />
                    {catCount > 0 && (
                      <Typography fontSize={10} color="text.disabled" mr={0.5}>{catCount}</Typography>
                    )}
                    {openCats[cat.id]
                      ? <ExpandLessIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                      : <ExpandMoreIcon sx={{ fontSize: 15, color: "text.disabled" }} />}
                  </ListItemButton>

                  {/* child items */}
                  <Collapse in={openCats[cat.id]}>
                    <Box sx={{ mb: 0.5 }}>
                      {cat.children.map((child) => {
                        const count = docs.filter(d => d.category === child.id).length;
                        const isSelected = selectedCat === child.id;
                        return (
                          <ListItemButton
                            key={child.id}
                            selected={isSelected}
                            onClick={() => setSelectedCat(child.id)}
                            sx={{
                              pl: 4.5, mx: 1, py: 0.4, borderRadius: 1.5, minHeight: 32,
                              "&.Mui-selected": { bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY },
                              "&.Mui-selected:hover": { bgcolor: alpha(PRIMARY, 0.15) },
                            }}>
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{ fontSize: 12.5, fontWeight: isSelected ? 700 : 400, color: isSelected ? PRIMARY : "text.primary" }}
                            />
                            {count > 0 && (
                              <Typography fontSize={11} color={isSelected ? PRIMARY : "text.disabled"} fontWeight={isSelected ? 700 : 400}>
                                {count}
                              </Typography>
                            )}
                          </ListItemButton>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ══════════ MAIN CONTENT ══════════ */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>

          {/* ── topbar ── */}
          <Box sx={{
            px: 3, py: 0, bgcolor: "#fff",
            borderBottom: "1px solid", borderColor: "divider",
            display: "flex", alignItems: "center", gap: 1.5,
            height: 60, flexShrink: 0,
          }}>
            {/* search */}
            <TextField
              size="small"
              placeholder="Search documents, tags…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ width: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* type filter */}
            <FormControl size="small" sx={{ width: 130 }}>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={filterType} onChange={e => setFilterType(e.target.value)}>
                <MenuItem value="All">All Types</MenuItem>
                <MenuItem value="SOP">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#1A9B7F" }} />
                    <span>SOP</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="Policy">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#2563eb" }} />
                    <span>Policy</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>

            {/* status filter */}
            <FormControl size="small" sx={{ width: 148 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <MenuItem value="All">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Under Review">Under Review</MenuItem>
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Archived">Archived</MenuItem>
              </Select>
            </FormControl>

            {/* refresh */}
            <Tooltip title="Refresh list">
              <span>
                <IconButton onClick={loadDocs} disabled={loading} size="small">
                  {loading ? <CircularProgress size={16} sx={{ color: PRIMARY }} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </span>
            </Tooltip>

            {/* spacer */}
            <Box flex={1} />

            {/* result count */}
            {!loading && (
              <Typography variant="body2" color="text.secondary" fontSize={12}>
                {filtered.length} document{filtered.length !== 1 ? "s" : ""}
                {selectedCat && <> &nbsp;·&nbsp; filtered</>}
              </Typography>
            )}

            {/* new document */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ bgcolor: PRIMARY, "&:hover": { bgcolor: PRIMARY_DARK }, px: 2, height: 36 }}>
              New Document
            </Button>
          </Box>

          {/* ── active filter breadcrumb ── */}
          {(selectedCat || filterType !== "All" || filterStatus !== "All" || search) && (
            <Box sx={{ px: 3, py: 0.75, bgcolor: alpha(PRIMARY, 0.04), borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              <Typography fontSize={12} color="text.secondary">Showing:</Typography>
              {selectedCat && (
                <Chip size="small" label={flatCats.find(c => c.id === selectedCat)?.label || selectedCat}
                  onDelete={() => setSelectedCat(null)}
                  sx={{ height: 20, fontSize: 11, bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY_DARK }} />
              )}
              {filterType !== "All" && (
                <Chip size="small" label={`Type: ${filterType}`} onDelete={() => setFilterType("All")}
                  sx={{ height: 20, fontSize: 11 }} />
              )}
              {filterStatus !== "All" && (
                <Chip size="small" label={`Status: ${filterStatus}`} onDelete={() => setFilterStatus("All")}
                  sx={{ height: 20, fontSize: 11 }} />
              )}
              {search && (
                <Chip size="small" label={`"${search}"`} onDelete={() => setSearch("")}
                  sx={{ height: 20, fontSize: 11 }} />
              )}
              <Box flex={1} />
              <Button size="small" sx={{ color: "text.secondary", fontSize: 11, py: 0 }}
                onClick={() => { setSearch(""); setFilterType("All"); setFilterStatus("All"); setSelectedCat(null); }}>
                Clear all
              </Button>
            </Box>
          )}

          {/* detail loading overlay */}
          {viewLoad && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, py: 2, bgcolor: alpha(PRIMARY, 0.04), flexShrink: 0 }}>
              <CircularProgress size={18} sx={{ color: PRIMARY }} />
              <Typography fontSize={13} color="text.secondary">Loading document…</Typography>
            </Box>
          )}

          {/* ── document card grid ── */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
            {loading ? (
              <Grid container spacing={2}>
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <Grid item xs={12} sm={6} md={4} key={n}>
                    <Skeleton variant="rounded" height={210} sx={{ borderRadius: 3 }} />
                  </Grid>
                ))}
              </Grid>
            ) : filtered.length === 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60%", gap: 2 }}>
                <Box sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: alpha(PRIMARY, 0.07), display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <DescriptionIcon sx={{ fontSize: 40, color: alpha(PRIMARY, 0.4) }} />
                </Box>
                <Typography color="text.secondary" fontWeight={600}>No documents found</Typography>
                <Typography fontSize={13} color="text.disabled">Try adjusting your search or filters</Typography>
                <Button variant="outlined" size="small" sx={{ borderColor: PRIMARY, color: PRIMARY, mt: 1 }}
                  onClick={() => { setSearch(""); setFilterType("All"); setFilterStatus("All"); setSelectedCat(null); }}>
                  Clear Filters
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filtered.map((doc) => (
                  <Grid item xs={12} sm={6} md={4} key={doc.id}>
                    <Card sx={{
                      height: "100%", display: "flex", flexDirection: "column",
                      borderRadius: 3, border: "1.5px solid", borderColor: "divider",
                      transition: "all 0.18s ease",
                      "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.1)", borderColor: TYPE_COLORS[doc.type], transform: "translateY(-3px)" },
                    }}>
                      {/* card top accent bar */}
                      <Box sx={{ height: 4, bgcolor: TYPE_COLORS[doc.type], borderRadius: "12px 12px 0 0" }} />

                      {/* card header */}
                      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                          <Chip
                            label={doc.type}
                            size="small"
                            sx={{ bgcolor: TYPE_COLORS[doc.type], color: "#fff", fontWeight: 700, fontSize: 11, height: 22 }}
                          />
                          <Chip
                            label={doc.status}
                            size="small"
                            color={STATUS_COLORS[doc.status]}
                            variant="outlined"
                            sx={{ fontSize: 10, height: 22 }}
                          />
                        </Stack>

                        <Typography
                          variant="subtitle2" fontWeight={700} title={doc.title}
                          sx={{ lineHeight: 1.35, mb: 0.5, fontSize: 13.5,
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {doc.title}
                        </Typography>

                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="caption" color="text.disabled" fontSize={11}>{doc.categoryLabel}</Typography>
                          <Typography variant="caption" color="text.disabled">•</Typography>
                          <Typography variant="caption" color="text.disabled" fontSize={11}>{doc.version}</Typography>
                        </Stack>
                      </Box>

                      <Divider />

                      {/* description */}
                      <CardContent sx={{ px: 2.5, py: 1.5, flex: 1 }}>
                        <Typography
                          variant="caption" color="text.secondary" fontSize={12} lineHeight={1.6}
                          sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {doc.description || "No description provided."}
                        </Typography>
                        {doc.tags.length > 0 && (
                          <Stack direction="row" flexWrap="wrap" gap={0.5} mt={1.5}>
                            {doc.tags.slice(0, 3).map(t => (
                              <Chip key={t} label={t} size="small"
                                sx={{ fontSize: 10, height: 18, bgcolor: alpha(PRIMARY, 0.08), color: PRIMARY_DARK, fontWeight: 500 }} />
                            ))}
                            {doc.tags.length > 3 && (
                              <Chip label={`+${doc.tags.length - 3}`} size="small"
                                sx={{ fontSize: 10, height: 18, bgcolor: "#f1f5f9", color: "text.secondary" }} />
                            )}
                          </Stack>
                        )}
                      </CardContent>

                      {/* card actions */}
                      <Box sx={{ px: 2, pb: 2, pt: 1, borderTop: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Stack direction="row" spacing={0}>
                          <Tooltip title="Print">
                            <IconButton size="small" onClick={() => handlePrint(doc)} sx={{ color: "text.secondary" }}>
                              <PrintIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy text">
                            <IconButton size="small" onClick={() => { navigator.clipboard?.writeText(buildCopyText(doc)); toast("Copied!"); }} sx={{ color: "text.secondary" }}>
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(doc)} sx={{ color: "text.secondary" }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => confirmDelete(doc)} sx={{ color: "#ef4444" }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => openView(doc.id)}
                          startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                          sx={{ bgcolor: TYPE_COLORS[doc.type], "&:hover": { filter: "brightness(0.88)" }, fontSize: 11.5, px: 1.5, height: 30, borderRadius: 1.5 }}>
                          View
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      </Box>

      {/* ══════════ Create / Edit Dialog ══════════ */}
      <Dialog open={!!editId} onClose={() => { if (!saving) { setEditId(null); setUploadState({ loading: false, file: null, dragOver: false }); } }}
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

        {/* ── title bar ── */}
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography fontWeight={700} fontSize={17} color="#fff">
                  {editId === "new" ? "Create New Document" : "Edit Document"}
                </Typography>
                <Typography fontSize={12} color={alpha("#fff", 0.75)} mt={0.3}>
                  Fill in all required fields then add procedure steps below
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => !saving && setEditId(null)}
                sx={{ color: "#fff", bgcolor: alpha("#fff", 0.12), "&:hover": { bgcolor: alpha("#fff", 0.22) } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: "#F7F9FB" }}>


          {/* ── File Upload Zone ── */}
<Box sx={{ px: 3, pt: 3, pb: 0 }}>
  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
    <Box sx={{ width: 4, height: 18, bgcolor: '#0ea5e9', borderRadius: 1 }} />
    <Typography fontWeight={700} fontSize={13} color="#0369a1"
      textTransform="uppercase" letterSpacing={0.8}>
      Import from File <Typography component="span" fontSize={11}
        color="text.disabled" textTransform="none" letterSpacing={0}>(optional — auto-fills form)</Typography>
    </Typography>
  </Stack>

  <Box
    onDragOver={e => { e.preventDefault(); setUploadState(s => ({ ...s, dragOver: true })); }}
    onDragLeave={() => setUploadState(s => ({ ...s, dragOver: false }))}
    onDrop={e => {
      e.preventDefault();
      setUploadState(s => ({ ...s, dragOver: false }));
      const file = e.dataTransfer.files[0];
      if (file) handleFileUpload(file);
    }}
    onClick={() => document.getElementById('sop-file-input').click()}
    sx={{
      border: '2px dashed',
      borderColor: uploadState.dragOver ? '#0ea5e9' : uploadState.file ? PRIMARY : '#cbd5e1',
      borderRadius: 2.5,
      p: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      cursor: 'pointer',
      bgcolor: uploadState.dragOver ? alpha('#0ea5e9', 0.05) : uploadState.file ? alpha(PRIMARY, 0.04) : '#fafafa',
      transition: 'all 0.2s',
      '&:hover': { borderColor: '#0ea5e9', bgcolor: alpha('#0ea5e9', 0.04) }
    }}
  >
    <input
      id="sop-file-input"
      type="file"
      accept=".docx,.doc,.txt"
      style={{ display: 'none' }}
      onChange={e => { const f = e.target.files[0]; if (f) handleFileUpload(f); e.target.value = ''; }}
    />

    {uploadState.loading ? (
      <>
        <CircularProgress size={28} sx={{ color: PRIMARY, flexShrink: 0 }} />
        <Box>
          <Typography fontWeight={600} fontSize={13} color={PRIMARY}>
            Extracting content…
          </Typography>
          <Typography fontSize={11} color="text.secondary">
            Reading document structure and sections
          </Typography>
        </Box>
      </>
    ) : uploadState.file ? (
      <>
        <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: alpha(PRIMARY, 0.12),
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <InsertDriveFileIcon sx={{ color: PRIMARY, fontSize: 22 }} />
        </Box>
        <Box flex={1}>
          <Typography fontWeight={700} fontSize={13} color={PRIMARY}>
            {uploadState.file.name}
          </Typography>
          <Typography fontSize={11} color="text.secondary">
            {(uploadState.file.size / 1024).toFixed(1)} KB — form has been pre-filled. You can edit below.
          </Typography>
        </Box>
        <Chip label="Re-upload" size="small" variant="outlined"
          sx={{ fontSize: 10, color: 'text.secondary', borderColor: 'divider' }}
          onClick={e => { e.stopPropagation(); document.getElementById('sop-file-input').click(); }} />
      </>
    ) : (
      <>
        <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: alpha('#0ea5e9', 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <UploadFileIcon sx={{ color: '#0ea5e9', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography fontWeight={600} fontSize={13} color="text.primary">
            Drop a .docx or .txt file here, or click to browse
          </Typography>
          <Typography fontSize={11} color="text.secondary">
            Title, description, and procedure steps will be extracted automatically
          </Typography>
        </Box>
      </>
    )}
  </Box>
</Box>

{/* Divider between upload and identity */}
<Box sx={{ px: 3, pt: 2 }}>
  <Divider>
    <Typography fontSize={11} color="text.disabled" px={1}>or fill in manually</Typography>
  </Divider>
</Box>

          {/* ── Section 1: Document Identity ── */}
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box sx={{ width: 4, height: 18, bgcolor: PRIMARY, borderRadius: 1 }} />
              <Typography fontWeight={700} fontSize={13} color={PRIMARY_DARK} textTransform="uppercase" letterSpacing={0.8}>
                Document Identity
              </Typography>
            </Stack>

            {/* Row 1: Title (full width) */}
            <TextField
              fullWidth
              label="Document Title *"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              sx={{ mb: 2, bgcolor: "#fff" }}
              placeholder="e.g. Medication Administration SOP"
            />

            {/* Row 2: Type | Category | Version | Status */}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth sx={{ bgcolor: "#fff" }}>
                  <InputLabel>Type *</InputLabel>
                  <Select label="Type *" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    <MenuItem value="SOP">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#1A9B7F" }} />
                        <span>SOP</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="Policy">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#2563eb" }} />
                        <span>Policy</span>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth sx={{ bgcolor: "#fff" }}>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <MenuItem value="Draft">Draft</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Under Review">Under Review</MenuItem>
                    <MenuItem value="Archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="Version" value={form.version} sx={{ bgcolor: "#fff" }}
                  onChange={e => setForm(f => ({ ...f, version: e.target.value }))}
                  placeholder="v1.0" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth type="date" label="Review Date" value={form.review_date}
                  onChange={e => setForm(f => ({ ...f, review_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }} sx={{ bgcolor: "#fff" }} />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* ── Section 2: Classification ── */}
          <Box sx={{ px: 3, py: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box sx={{ width: 4, height: 18, bgcolor: "#2563eb", borderRadius: 1 }} />
              <Typography fontWeight={700} fontSize={13} color="#1e40af" textTransform="uppercase" letterSpacing={0.8}>
                Classification & Ownership
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ bgcolor: "#fff" }}>
                  <InputLabel>Category *</InputLabel>
                  <Select label="Category *" value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {flatCats.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Department / Area" value={form.department} sx={{ bgcolor: "#fff" }}
                  onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                  placeholder="e.g. All Wards, A&E, Theatre" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Author / Prepared By" value={form.author} sx={{ bgcolor: "#fff" }}
                  onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
                  placeholder="e.g. Dr. Mensah" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Approved By" value={form.approved_by} sx={{ bgcolor: "#fff" }}
                  onChange={e => setForm(f => ({ ...f, approved_by: e.target.value }))}
                  placeholder="e.g. Medical Director" />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* ── Section 3: Description & Tags ── */}
          <Box sx={{ px: 3, py: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box sx={{ width: 4, height: 18, bgcolor: "#d97706", borderRadius: 1 }} />
              <Typography fontWeight={700} fontSize={13} color="#92400e" textTransform="uppercase" letterSpacing={0.8}>
                Description & Tags
              </Typography>
            </Stack>

            <TextField fullWidth multiline rows={3} label="Description / Purpose" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Briefly describe what this document covers and why it exists"
              sx={{ mb: 2, bgcolor: "#fff" }} />

            <TextField fullWidth label="Tags" value={form.tags}
              onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
              placeholder="e.g. medication, safety, nurses, pharmacy"
              helperText="Separate tags with commas — used for search and filtering"
              sx={{ bgcolor: "#fff" }} />
          </Box>

          <Divider />

          {/* ── Section 4: Steps ── */}
          <Box sx={{ px: 3, py: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 4, height: 18, bgcolor: "#7c3aed", borderRadius: 1 }} />
                <Typography fontWeight={700} fontSize={13} color="#5b21b6" textTransform="uppercase" letterSpacing={0.8}>
                  {form.type === "SOP" ? "Procedure Steps" : "Policy Sections"}
                </Typography>
                <Chip label={form.steps.length} size="small"
                  sx={{ bgcolor: alpha("#7c3aed", 0.12), color: "#5b21b6", fontWeight: 700, height: 20, fontSize: 11 }} />
              </Stack>
              <Button size="small" startIcon={<AddIcon />} onClick={addStep}
                variant="outlined"
                sx={{ color: PRIMARY, borderColor: PRIMARY, "&:hover": { bgcolor: alpha(PRIMARY, 0.06), borderColor: PRIMARY_DARK }, fontSize: 12 }}>
                Add Step
              </Button>
            </Stack>

            <Stack spacing={1.5}>
              {form.steps.map((step, i) => (
                <Paper key={i} elevation={0}
                  sx={{ borderRadius: 2.5, border: "1px solid", borderColor: alpha("#7c3aed", 0.2), overflow: "hidden" }}>
                  {/* step header */}
                  <Box sx={{ px: 2, py: 1, bgcolor: alpha("#7c3aed", 0.06), borderBottom: "1px solid", borderColor: alpha("#7c3aed", 0.12) }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography fontSize={11} fontWeight={700} color="#fff">{i + 1}</Typography>
                        </Box>
                        <Typography fontSize={12} fontWeight={700} color="#5b21b6">
                          Step {i + 1}
                        </Typography>
                      </Stack>
                      {form.steps.length > 1 && (
                        <IconButton size="small" onClick={() => removeStep(i)}
                          sx={{ color: "#ef4444", bgcolor: alpha("#ef4444", 0.08), "&:hover": { bgcolor: alpha("#ef4444", 0.15) }, width: 24, height: 24 }}>
                          <CloseIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                  {/* step body */}
                  <Box sx={{ p: 2, bgcolor: "#fff" }}>
                    <TextField fullWidth size="small" label="Step Title"
                      value={step.label} onChange={e => updateStep(i, "label", e.target.value)}
                      sx={{ mb: 1.5 }} placeholder="e.g. Verify Doctor's Order" />
                    <TextField fullWidth size="small" multiline rows={3} label="Detailed Instructions"
                      value={step.content} onChange={e => updateStep(i, "content", e.target.value)}
                      placeholder="Describe exactly what should be done in this step…" />
                  </Box>
                </Paper>
              ))}
            </Stack>

            {/* add step hint */}
            <Button fullWidth onClick={addStep} startIcon={<AddIcon />}
              sx={{ mt: 2, py: 1.2, border: "2px dashed", borderColor: alpha(PRIMARY, 0.3), color: PRIMARY, borderRadius: 2,
                "&:hover": { borderColor: PRIMARY, bgcolor: alpha(PRIMARY, 0.04) }, fontWeight: 600, fontSize: 13 }}>
              + Add Another Step
            </Button>
          </Box>

        </DialogContent>

        {/* ── footer ── */}
        <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fff", borderTop: "1px solid", borderColor: "divider", gap: 1 }}>
          <Button onClick={() => setEditId(null)} disabled={saving}
            sx={{ color: "text.secondary", minWidth: 100 }}>
            Cancel
          </Button>
          <Box flex={1} />
          <Button variant="contained" onClick={saveDoc} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
            sx={{ bgcolor: PRIMARY, "&:hover": { bgcolor: PRIMARY_DARK }, minWidth: 160, py: 1.1, fontSize: 14, fontWeight: 700 }}>
            {saving ? "Saving…" : editId === "new" ? "Create Document" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════ Snackbar ══════════ */}
      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </ThemeProvider>
  );
}